import { useEffect } from "react";
import { Navigate, NavLink, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, Images, MessageSquareQuote, FileText, Inbox, Settings, LogOut, ExternalLink, PanelsTopLeft, Menu, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const GROUPS = [{ title: null, items: [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/admin/pagine", label: "Pagine del sito", icon: PanelsTopLeft, id: "pagine" },
  { to: "/admin/menu", label: "Menu e navigazione", icon: Menu, id: "menu" },
  { to: "/admin/portfolio", label: "Portfolio", icon: Images, id: "portfolio" },
  { to: "/admin/area-clienti", label: "Area clienti", icon: Lock, id: "area-clienti" },
  { to: "/admin/recensioni", label: "Recensioni", icon: MessageSquareQuote, id: "recensioni" },
  { to: "/admin/preventivi", label: "Preventivi PDF", icon: FileText, id: "preventivi" },
  { to: "/admin/contatti", label: "Contatti", icon: Inbox, id: "contatti" },
  { to: "/admin/impostazioni", label: "Impostazioni", icon: Settings, id: "impostazioni" },
] }];
const linkCls = ({ isActive }) =>
  `flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-md whitespace-nowrap transition-colors duration-150 ${isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`;

export default function AdminLayout() {
  const { user, logout, refresh } = useAuth();
  useEffect(() => { if (user === undefined) refresh(); }, [user, refresh]);
  if (user === null || user === undefined) return <div className="min-h-screen bg-slate-50" />;
  if (user === false) return <Navigate to="/admin/login" replace />;
  return (
    <div className="admin-root min-h-screen bg-slate-50 text-slate-900 lg:flex" data-testid="admin-layout">
      <aside className="bg-white border-r border-slate-200 lg:w-60 lg:fixed lg:inset-y-0 flex lg:flex-col z-30 sticky top-0 border-b lg:border-b-0">
        <div className="hidden lg:flex items-center px-5 h-16 border-b border-slate-200">
          <img src="/brand/logo-grey.webp" alt="Studio Fotografico Santarelli" className="h-7 w-auto" data-testid="admin-logo" />
        </div>
        <nav className="flex lg:flex-col gap-1 lg:gap-0 px-3 py-2 lg:py-4 overflow-x-auto lg:overflow-y-auto scrollbar-none flex-1">
          {GROUPS.map((g, gi) => (
            <div key={gi} className="flex lg:flex-col gap-1 lg:gap-0.5 lg:mb-4">
              {g.title && <p className="hidden lg:block px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{g.title}</p>}
              {g.items.map((m) => (
                <NavLink key={m.to} to={m.to} end={m.end} className={linkCls} data-testid={`admin-nav-${m.id}`}>
                  <m.icon size={15} strokeWidth={1.8} /> {m.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="hidden lg:block px-3 py-3 border-t border-slate-200 space-y-0.5">
          <Link to="/" target="_blank" className={linkCls({})} data-testid="admin-view-site"><ExternalLink size={15} /> Vedi il sito</Link>
          <button onClick={logout} className={`${linkCls({})} w-full`} data-testid="admin-logout-btn"><LogOut size={15} /> Esci</button>
          <p className="px-3 pt-2 text-[11px] text-slate-400 truncate">{user.email}</p>
        </div>
      </aside>
      <main className="flex-1 lg:ml-60 min-w-0">
        <div className="lg:hidden flex justify-between items-center px-5 py-3 border-b border-slate-200 bg-white">
          <img src="/brand/logo-grey.webp" alt="Studio Fotografico Santarelli" className="h-6 w-auto" data-testid="admin-logo-mobile" />
          <button onClick={logout} className="text-xs text-slate-500 flex items-center gap-1" data-testid="admin-logout-btn-mobile"><LogOut size={14} /> Esci</button>
        </div>
        <div className="p-5 sm:p-8 max-w-[1400px]"><Outlet /></div>
      </main>
    </div>
  );
}

export const AdminTitle = ({ title, subtitle, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
    <div>
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900" data-testid="admin-page-title">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
    <div className="flex gap-2 flex-wrap items-center">{children}</div>
  </div>
);
